import { useState } from 'react';
import { readiness } from './staff-readiness.ts';

import { explainFailure } from './client.ts';
import { kitText, type KitMessageKey } from './messages.ts';
import { textArray } from './text-array.ts';
import { Callout, Chip, EmailInput, Empty, Facts, Field, Loading, Modal, Panel, PasswordInput, PASSWORD_MIN_LENGTH, rowOpen, SearchRow, Select, TableScroll, TextInput } from './ui.tsx';
import { useLoad } from './useLoad.ts';

/**
 * 0232 — what the operator console tells this screen about brands.
 *
 * `brands` is the operator's own list, for the picker. `lockedBrandId` is set
 * inside a brand console: the list is then filtered to that brand, a new
 * person is limited to it from the start, and nobody's limit can be widened
 * from there — that is the operator console's job.
 */
export interface BrandScoping {
  readonly brands: readonly StaffBrand[];
  readonly lockedBrandId?: string;
  readonly lockedBrandName?: string;
}

/** "All brands", or the names of the ones they are limited to. */
function brandsLabel(brands: readonly StaffBrand[] | undefined): string {
  return brands && brands.length > 0
    ? brands.map((b) => b.name).join(', ')
    : kitText('staff.brands.all');
}

/**
 * The brand checklist behind both the add form and the person's own panel.
 * Nothing ticked means unrestricted — the default, and what every existing
 * person has — so the empty state reads as a statement, not as a form left
 * blank.
 */
function BrandPicker({ brands, chosen, onChange, disabled }: {
  brands: readonly StaffBrand[];
  chosen: readonly string[];
  onChange: (next: readonly string[]) => void;
  disabled?: boolean;
}) {
  return (
    <div className="brand-picker">
      {brands.map((b) => {
        const on = chosen.includes(b.id);
        return (
          <label key={b.id} className="check-option">
            <input
              type="checkbox"
              checked={on}
              disabled={disabled}
              onChange={(e) =>
                onChange(e.currentTarget.checked ? [...chosen, b.id] : chosen.filter((x) => x !== b.id))}
            />
            {b.name}
          </label>
        );
      })}
      <span className="faint">
        {chosen.length === 0 ? kitText('staff.brands.pickerAll') : kitText('staff.brands.pickerSome', { n: String(chosen.length) })}
      </span>
    </div>
  );
}

/**
 * Adding people, shared by both consoles.
 *
 * The rules are identical whether the platform is adding its own staff or an
 * operator is adding theirs — §3.7's must-change password and mandatory MFA,
 * §3.3's separation of duties, §3.9's line between the two — so there is one
 * screen and the API decides which side is asking.
 */

/** 0232 — a brand a member of staff may be limited to. */
export interface StaffBrand {
  readonly id: string;
  readonly name: string;
}

export interface StaffPerson {
  id: string;
  email: string;
  actor_class: string;
  status: string;
  roles: readonly string[];
  mfa_enrolled: boolean;
  must_change_password: boolean;
  /** 0232 — the brands they are limited to; empty (or absent, from an older
   *  server) means the whole operator. */
  brands?: readonly StaffBrand[];
}

export interface StaffApi {
  /** `brandId` narrows the list to people who act on that brand — those
   *  limited to it, and those limited to nothing. */
  staff(search?: string, brandId?: string): Promise<{
    items: StaffPerson[];
    /** Whether the server is actually enforcing the second factor. Optional so
     *  an older server, which does not send it, is read as enforcing. */
    mfa_enforced?: boolean;
  }>;
  roles(): Promise<{
    items: { id: string; code: string; permissions: readonly string[] }[];
    note: string;
  }>;
  addStaff(body: {
    email: string;
    role_id: string;
    temporary_password: string;
    /** 0232 — limit the new person to these brands from the start. */
    brand_ids?: readonly string[];
  }): Promise<{
    email: string;
    role: string;
    next_steps: string;
  }>;
  /** 0232 — replace the brands a person is limited to; an empty list lifts
   *  the limit. Optional: the platform's own staff have no brands. */
  setStaffBrands?(userId: string, brandIds: readonly string[]): Promise<{ user_id: string; brands: readonly string[] }>;
  removeStaff(
    userId: string,
    body: { permanent: boolean; confirm_email?: string },
  ): Promise<{ removed?: boolean; purged?: boolean; user_id: string }>;
  /** §3.7 — clears a lost or replaced authenticator, so they are asked to set
   *  up a new one on their next sign-in. Not an MFA opt-out. */
  resetMfa(userId: string): Promise<{ reset: boolean; user_id: string; message: string }>;
}

/**
 * §3.9 — the three platform-staff tiers, in the words a person uses about
 * them rather than the role code. PLATFORM_SUPER_ADMIN never reaches this —
 * it is filtered out of `roles()` and out of `staff()` before either
 * response gets here — but the label exists anyway for the one place its
 * code could still surface: an audit record naming it.
 */
const ROLE_CODES = [
  'PLATFORM_SUPER_ADMIN',
  'PLATFORM_SUPPORT',
  'PLATFORM_USER',
  'OPERATOR_OWNER',
  'AFFILIATE_DIRECTOR',
  'AFFILIATE_MANAGER',
  'FINANCE',
  'COMPLIANCE',
  'FRAUD_ANALYST',
  'SUPPORT',
] as const;

const KNOWN_ROLES: ReadonlySet<string> = new Set(ROLE_CODES);

/**
 * The word for a role, or the role code when there is no word for it.
 *
 * The fallback is the point of the set above. A role this build has never
 * heard of surfaces as its own code — `FRAUD_ANALYST`, unchanged in every
 * language — rather than as a lookup miss or a guess, which is §2.8b's line
 * held at the one place a code can still reach a screen.
 */
export function roleLabel(code: string): string {
  return KNOWN_ROLES.has(code) ? kitText(`role.${code}` as KitMessageKey) : code;
}

/**
 * The role an operator holds exactly one of, and the one nothing here may
 * remove: an operator with no owner has nobody who can add staff, nobody the
 * platform can write to, and — where the owner was the only member of the
 * team — nobody who can sign in at all. `staff.ts` refuses it server-side
 * (CANNOT_REMOVE_OWNER); this is what stops the button being there to press.
 */
const OPERATOR_OWNER = 'OPERATOR_OWNER';

function AddStaffForm({
  api,
  roles,
  brandScoping,
  onDone,
}: {
  api: StaffApi;
  roles: { items: { id: string; code: string; permissions: readonly string[] }[]; note: string };
  brandScoping?: BrandScoping;
  onDone: (nextSteps: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState('');
  const [temp, setTemp] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  // Inside a brand console the new person is that brand's from the start;
  // the operator console offers the list, unrestricted by default.
  const locked = brandScoping?.lockedBrandId;
  const [brandIds, setBrandIds] = useState<readonly string[]>(locked ? [locked] : []);
  const offersBrands = brandScoping !== undefined && !locked && brandScoping.brands.length > 1;

  return (
    <div className="form">
      {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}

      <div className="form-row">
        <Field label={kitText('field.email')}>
          <EmailInput value={email} onChange={setEmail} placeholder="name@example.com" />
        </Field>

        <Field
          label={kitText('staff.field.role')}
          hint={
            textArray(roles.items.find((r) => r.id === roleId)?.permissions).join(', ') ||
            kitText('staff.field.role.hint')
          }
        >
          <Select
            searchable={false}
            value={roleId}
            onChange={setRoleId}
            options={[
              { value: '', label: kitText('staff.field.role.choose') },
              ...roles.items.map((r) => ({ value: r.id, label: roleLabel(r.code) })),
            ]}
          />
        </Field>

        <Field
          label={kitText('staff.field.tempPassword')}
          hint={kitText('staff.field.tempPassword.hint')}
        >
          <PasswordInput
            value={temp}
            onChange={setTemp}
            autoComplete="new-password"
            minLength={PASSWORD_MIN_LENGTH}
          />
        </Field>
      </div>

      {offersBrands ? (
        <Field label={kitText('staff.brands.field')} hint={kitText('staff.brands.field.hint')}>
          <BrandPicker brands={brandScoping.brands} chosen={brandIds} onChange={setBrandIds} />
        </Field>
      ) : null}
      {locked ? (
        <Callout tone="note">
          {kitText('staff.brands.lockedNote', { brand: brandScoping?.lockedBrandName ?? '' })}
        </Callout>
      ) : null}

      <Callout>{roles.note}</Callout>

      <div className="form-actions">
        <button
          className="primary"
          /*
           * The password is optional: blank means the platform mints one and
           * mails it. A half-typed one is still refused, because the server
           * would refuse it too and the message would arrive as a failure
           * after the click rather than as a rule before it.
           */
          disabled={
            busy || email === '' || roleId === '' ||
            (temp !== '' && temp.length < PASSWORD_MIN_LENGTH)
          }
          onClick={() => {
            setBusy(true);
            setFailure(null);
            api
              .addStaff({
                email, role_id: roleId, temporary_password: temp.trim(),
                ...(brandIds.length > 0 ? { brand_ids: brandIds } : {}),
              })
              .then((r) => onDone(r.next_steps))
              .catch((e: unknown) => setFailure(e))
              .finally(() => setBusy(false));
          }}
        >
          {busy ? kitText('action.adding') : kitText('staff.add')}
        </button>
      </div>
    </div>
  );
}

/**
 * Two different actions behind one button, not one action with a flag
 * nobody reads — the same shape the platform's own operator-delete screen
 * uses. Unchecked closes the account (`status = 'CLOSED'`): reversible in
 * principle, and enough on its own — a closed account cannot authenticate,
 * full stop. Checked runs the actual purge and cannot be undone, so the
 * confirm field has to match the person's exact email either way: a
 * misclick should cost a re-type, not an account.
 */
function RemoveStaffModal({
  api,
  person,
  reversibleOnly = false,
  onClose,
  onDone,
}: {
  api: StaffApi;
  person: { id: string; email: string; status: string };
  /** A platform user's own session may close an account but never erase it. */
  reversibleOnly?: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const alreadyRemoved = person.status !== 'ACTIVE';
  const [typed, setTyped] = useState('');
  const [permanent, setPermanent] = useState(alreadyRemoved);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const matches = typed.length > 0 && typed === person.email;

  return (
    <Modal title={kitText('staff.remove.title', { email: person.email })} onClose={onClose}>
      {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}

      {alreadyRemoved ? (
        <Callout tone="caution">{kitText('staff.remove.alreadyClosed')}</Callout>
      ) : null}

      <Callout tone={permanent ? 'error' : 'caution'}>
        {permanent ? kitText('staff.remove.permanent') : kitText('staff.remove.reversible')}
      </Callout>

      <Field label={kitText('staff.remove.confirmField')} hint={person.email}>
        <TextInput value={typed} onChange={setTyped} placeholder={person.email} />
      </Field>

      {alreadyRemoved || reversibleOnly ? null : (
        <label className="copy">
          <input
            type="checkbox"
            checked={permanent}
            onChange={(e) => setPermanent(e.target.checked)}
          />
          {' '}{kitText('staff.remove.permanentCheckbox')}
        </label>
      )}

      <div className="form-actions">
        <button className="copy" disabled={busy} onClick={onClose}>
          {kitText('action.cancel')}
        </button>
        <button
          className="danger"
          disabled={busy || !matches}
          onClick={() => {
            setBusy(true);
            setFailure(null);
            api
              .removeStaff(person.id, {
                permanent: alreadyRemoved || permanent,
                confirm_email: typed,
              })
              .then(() => onDone())
              .catch((e: unknown) => setFailure(e))
              .finally(() => setBusy(false));
          }}
        >
          {busy
            ? kitText('action.removing')
            : alreadyRemoved || permanent
              ? kitText('staff.remove.permanentAction')
              : kitText('staff.remove.action')}
        </button>
      </div>
    </Modal>
  );
}

/**
 * One person, in full, from clicking their row.
 *
 * The list has to stay scannable, so it shows a state and a single sentence
 * and truncates the rest. That is the wrong place to find out *why* somebody
 * cannot sign in, which is the question an administrator actually opens this
 * screen with — so the row opens, rather than making them infer it from a
 * chip and a column they may have scrolled past.
 */
function StaffDetailModal({
  api,
  person,
  mfaEnforced,
  isSelf,
  canWrite,
  selfSession,
  brandScoping,
  onClose,
  onReset,
}: {
  api: StaffApi;
  person: StaffPerson;
  mfaEnforced: boolean;
  /** 0232 — present in an operator console; the limit is editable only in
   *  the unlocked one, because a brand console must not widen anybody's. */
  brandScoping?: BrandScoping;
  /** Their own row. The API refuses it too; this stops the button existing. */
  isSelf: boolean;
  /** False for the platform's read-only role: the row opens, but nothing in it writes. */
  canWrite: boolean;
  /** A platform user's own session: the brand limit is editable, a second factor is not reset. */
  selfSession: boolean;
  onClose: () => void;
  /** The list re-reads: the row's state chip changes with the reset. */
  onReset: () => void;
}) {
  const state = readiness(person, mfaEnforced);
  /*
   * §3.7 — the reset, where the fact it changes is already on screen.
   *
   * Two presses rather than one, and inline rather than a second modal: this
   * panel is itself a modal, and a dialog opening on top of a dialog puts the
   * question somewhere the answer's context is hidden behind it. Nothing is
   * optimistic — the fact above goes on saying "Set up" until the write has
   * landed and the list has re-read it.
   */
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const canReset = canWrite && !selfSession && person.mfa_enrolled && !isSelf;
  /*
   * 0232 — the brands they are limited to, editable where this console may
   * widen a limit: the operator's own, with more than one brand to choose
   * between, on anybody but the owner (who acts on every brand, and whom the
   * API refuses to limit) and not on oneself.
   */
  const isOwner = textArray(person.roles).includes(OPERATOR_OWNER);
  const [brandIds, setBrandIds] = useState<readonly string[]>((person.brands ?? []).map((b) => b.id));
  const [savingBrands, setSavingBrands] = useState(false);
  const [brandsSaved, setBrandsSaved] = useState(false);
  const setStaffBrands = api.setStaffBrands?.bind(api);
  const canEditBrands =
    canWrite && brandScoping !== undefined && !brandScoping.lockedBrandId &&
    brandScoping.brands.length > 1 && !isOwner && !isSelf && setStaffBrands !== undefined;
  const brandsChanged =
    [...brandIds].sort().join() !== (person.brands ?? []).map((b) => b.id).sort().join();

  return (
    <Modal title={person.email} onClose={onClose}>
      <Facts
        items={[
          { label: kitText('field.email'), value: person.email },
          {
            label: kitText('staff.facts.roles'),
            value:
              textArray(person.roles).map(roleLabel).join(', ') ||
              kitText('staff.facts.roles.none'),
          },
          { label: kitText('staff.facts.state'), value: <Chip tone={state.tone}>{state.label}</Chip> },
          {
            label: kitText('staff.facts.authenticator'),
            value: person.mfa_enrolled
              ? kitText('staff.facts.authenticator.set')
              : mfaEnforced
                ? kitText('staff.facts.authenticator.unset')
                : kitText('staff.facts.authenticator.notAsked'),
          },
          {
            label: kitText('field.password'),
            value: person.must_change_password
              ? kitText('staff.facts.password.temporary')
              : kitText('staff.facts.password.own'),
          },
          ...(brandScoping
            ? [{
                label: kitText('staff.facts.brands'),
                value: isOwner ? kitText('staff.brands.owner') : brandsLabel(person.brands),
              }]
            : []),
        ]}
      />
      {state.why ? <Callout tone="note">{state.why}</Callout> : null}

      {canEditBrands && setStaffBrands ? (
        <>
          <Field label={kitText('staff.brands.field')} hint={kitText('staff.brands.field.hint')}>
            <BrandPicker
              brands={brandScoping.brands}
              chosen={brandIds}
              onChange={(next) => { setBrandIds(next); setBrandsSaved(false); }}
              disabled={savingBrands}
            />
          </Field>
          {brandsSaved ? <Callout tone="note">{kitText('staff.brands.saved')}</Callout> : null}
          <div className="form-actions">
            <button
              className="primary"
              disabled={savingBrands || !brandsChanged}
              onClick={() => {
                setSavingBrands(true);
                setFailure(null);
                setStaffBrands(person.id, brandIds)
                  .then(() => { setBrandsSaved(true); onReset(); })
                  .catch((e: unknown) => setFailure(e))
                  .finally(() => setSavingBrands(false));
              }}
            >
              {savingBrands ? kitText('action.saving') : kitText('staff.brands.save')}
            </button>
          </div>
        </>
      ) : null}

      {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}
      {done ? <Callout tone="note">{done}</Callout> : null}

      {canReset && !done ? (
        asking ? (
          <>
            <Callout tone="caution">{kitText('staff.reset.warning')}</Callout>
            <div className="form-actions">
              <button className="copy" disabled={busy} onClick={() => setAsking(false)}>
                {kitText('action.cancel')}
              </button>
              <button
                className="danger"
                disabled={busy}
                onClick={() => {
                  setBusy(true);
                  setFailure(null);
                  api
                    .resetMfa(person.id)
                    .then((r) => { setDone(r.message); setAsking(false); onReset(); })
                    .catch((e: unknown) => setFailure(e))
                    .finally(() => setBusy(false));
                }}
              >
                {busy ? kitText('staff.reset.busy') : kitText('staff.reset.confirm')}
              </button>
            </div>
          </>
        ) : (
          <div className="form-actions">
            <button className="danger-quiet" onClick={() => setAsking(true)}>
              {kitText('staff.reset.action')}
            </button>
          </div>
        )
      ) : null}
    </Modal>
  );
}

export function Staff({
  api,
  scope,
  currentUserId,
  canWrite = true,
  selfSession = false,
  brandScoping,
}: {
  api: StaffApi;
  scope: 'platform' | 'operator';
  /** 0232 — the operator's brands, and which one this console is locked to. */
  brandScoping?: BrandScoping;
  /** Hides the "Remove" action on this row — the API already refuses acting on
   * one's own account (staff.ts's CANNOT_ACT_ON_SELF), this just stops the
   * button being there to click in the first place. */
  currentUserId?: string;
  /**
   * False for the platform's read-only role: the list stays, and every
   * control that changes something — Add, Remove, Reset authenticator — does
   * not. The row still opens; seeing why somebody cannot sign in is not a
   * write.
   */
  canWrite?: boolean;
  /**
   * A platform user in this console as themselves. With `canWrite` it leaves
   * only what the server still takes there: a person's brand limit, and a
   * reversible removal. Adding, resetting a second factor and erasing
   * somebody for good are not offered.
   */
  selfSession?: boolean;
}) {
  const [version, setVersion] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState<string | null>(null);
  const [removing, setRemoving] = useState<{ id: string; email: string; status: string } | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);

  const runSearch = () => setQuery(searchInput.trim());

  const lockedBrandId = brandScoping?.lockedBrandId;
  const loaded = useLoad(
    () => Promise.all([api.staff(query || undefined, lockedBrandId), api.roles()]),
    [query, version, lockedBrandId],
  );
  const showBrands = brandScoping !== undefined && brandScoping.brands.length > 1;
  /*
   * The head, the add button and the search box stay mounted while the list
   * loads: only the panel below shows the skeleton or the failure. Returning
   * `<Loading/>` for the whole screen swapped the search box out on every
   * search, so the focus that typed the query was dropped with it.
   */
  const ready = loaded.state === 'ready' ? loaded.value : null;
  const people = ready?.[0];
  const roles = ready?.[1];
  const mfaEnforced = people?.mfa_enforced !== false;

  return (
    <>
      <div className="page-head">
        <h1>{scope === 'platform' ? kitText('staff.title.platform') : kitText('staff.title.operator')}</h1>
        {canWrite && !selfSession ? (
          <div className="page-head-actions">
            <button className="primary" onClick={() => setAdding(true)}>
              {scope === 'platform'
                ? kitText('staff.addPerson.platform')
                : kitText('staff.addPerson.operator')}
            </button>
          </div>
        ) : null}
      </div>

      {canWrite && !selfSession && adding && roles ? (
        <Modal
          title={
            scope === 'platform'
              ? kitText('staff.addPerson.platform')
              : kitText('staff.addPerson.operator')
          }
          onClose={() => setAdding(false)}
        >
          <AddStaffForm
            api={api}
            roles={roles}
            {...(brandScoping ? { brandScoping } : {})}
            onDone={(nextSteps) => {
              setAdded(nextSteps);
              setAdding(false);
              setVersion((v) => v + 1);
            }}
          />
        </Modal>
      ) : null}

      {canWrite && removing ? (
        <RemoveStaffModal
          api={api}
          person={removing}
          reversibleOnly={selfSession}
          onClose={() => setRemoving(null)}
          onDone={() => {
            setRemoving(null);
            setVersion((v) => v + 1);
          }}
        />
      ) : null}

      {viewing && people ? (() => {
        const person = people.items.find((p) => p.id === viewing);
        return person ? (
          <StaffDetailModal
            api={api}
            person={person}
            mfaEnforced={mfaEnforced}
            isSelf={person.id === currentUserId}
            canWrite={canWrite}
            selfSession={selfSession}
            {...(brandScoping ? { brandScoping } : {})}
            onClose={() => setViewing(null)}
            onReset={() => setVersion((v) => v + 1)}
          />
        ) : null;
      })() : null}

      {added ? <Callout>{added}</Callout> : null}

      <SearchRow
        value={searchInput}
        onChange={setSearchInput}
        onSearch={runSearch}
        placeholder={kitText('staff.search.placeholder')}
      />

      <Panel
        title={kitText('staff.panel')}
        {...(people ? { aside: <span className="count count-badge">{people.items.length}</span> } : {})}
      >
        {!people ? (
          loaded.state === 'error'
            ? <Callout tone="error">{kitText('staff.loadFailed')}</Callout>
            : <Loading rows={2} />
        ) : people.items.length === 0 ? (
          <Empty>{query ? kitText('staff.empty.query') : kitText('staff.empty')}</Empty>
        ) : (
          <TableScroll>
            <table>
              <thead>
                <tr>
                  <th>{kitText('field.email')}</th>
                  <th>{kitText('staff.facts.roles')}</th>
                  {showBrands ? <th>{kitText('staff.facts.brands')}</th> : null}
                  <th>{kitText('staff.facts.state')}</th>
                  <th />
                  {/* The whole column is Remove — nothing else lives in it —
                      so with no write there is nothing for it to hold. */}
                  {canWrite ? <th className="col-action" /> : null}
                </tr>
              </thead>
              <tbody>
                {people.items.map((row) => {
                  const state = readiness(row, mfaEnforced);
                  return (
                    <tr
                      key={row.id}
                      className="row-clickable"
                      onClick={() => setViewing(row.id)}
                      {...rowOpen(() => setViewing(row.id))}
                    >
                      <td>{row.email}</td>
                      {/* Text, not a figure — right-aligning it under a
                          left-aligned heading was the mismatch. */}
                      <td className="faint">{textArray(row.roles).map(roleLabel).join(', ') || '—'}</td>
                      {showBrands ? (
                        <td className="faint">
                          {textArray(row.roles).includes(OPERATOR_OWNER)
                            ? kitText('staff.brands.owner')
                            : brandsLabel(row.brands)}
                        </td>
                      ) : null}
                      <td><Chip tone={state.tone}>{state.label}</Chip></td>
                      <td className="wrap">{state.why ?? ''}</td>
                      {canWrite ? (
                        <td className="num col-action">
                          {row.id === currentUserId ? (
                            <span className="faint">{kitText('staff.self')}</span>
                          ) : textArray(row.roles).includes(OPERATOR_OWNER) ? (
                            <span className="faint">{kitText('staff.owner.locked')}</span>
                          ) : selfSession && row.status !== 'ACTIVE' ? null : (
                            <button
                              className="link-button"
                              onClick={(e) => {
                                // The row opens the person; this button removes
                                // them. Letting the click reach both would open a
                                // panel behind the modal it just opened.
                                e.stopPropagation();
                                setRemoving({ id: row.id, email: row.email, status: row.status });
                              }}
                            >
                              {kitText('staff.remove.action')}
                            </button>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Panel>

      {/* §3.3 — refused at save, not found at audit. Worth saying, because an
          administrator whose grant is refused should know it is a rule and not
          a fault. Naming commission terms and payment, which only an operator
          console's roles combine — the platform's own roles never touch
          either, so a read-only platform viewer has no rule here to be told. */}
      {scope === 'operator' ? <Callout>{kitText('staff.combinations')}</Callout> : null}
    </>
  );
}
